import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Tabs, Card, Tag, Input, Table, Steps, Progress, Collapse, Tooltip, Badge,
  Drawer, Descriptions, Row, Col, Space, Divider, Statistic, Empty, Spin,
  Button, Typography, message,
} from 'antd';
import {
  ArrowLeftOutlined, CheckCircleOutlined, RollbackOutlined,
  NodeIndexOutlined, ApartmentOutlined, TableOutlined,
  LinkOutlined, SearchOutlined, InfoCircleOutlined,
  RightOutlined, SwapOutlined, DatabaseOutlined,
  ClusterOutlined, FundProjectionScreenOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

// ─── Type Definitions ────────────────────────────────────────────────────────

interface ProcessStep {
  formId: string;
  name: string;
  type: 'process' | 'approval' | 'reversal';
  status: 'done' | 'progress' | 'notstarted';
  isHub?: boolean;
}

interface BusinessProcess {
  id: string;
  name: string;
  nameEn: string;
  color: string;
  description: string;
  chains: string[];
  steps: ProcessStep[];
}

interface UpstreamDownstream {
  formId: string;
  name: string;
  tables: string[];
  relation: string;
}

interface ApprovalStep {
  step: number;
  formId: string;
  action: string;
  statusField: string;
  value: string;
}

interface ModuleInfo {
  name: string;
  domain: string;
  status: 'done' | 'progress' | 'notstarted';
  placement: string;
  chains: string[];
  writeTables: string[];
  readTables: string[];
  upstream: UpstreamDownstream[];
  downstream: UpstreamDownstream[];
  approvalChain?: ApprovalStep[];
}

interface Chain {
  id: string;
  name: string;
  domain: string;
  status: string;
  modules: string[];
  moduleCount: number;
  doneCount: number;
  description: string;
}

interface DependencyEdge {
  from: string;
  fromDomain: string;
  to: string;
  toDomain: string;
  tables: string[];
  meaning: string;
}

interface DependencyMatrix {
  domains: string[];
  domainLabels: Record<string, string>;
  matrix: number[][];
  edges: DependencyEdge[];
}

interface MasterDataItem {
  table: string;
  name: string;
  formId: string | null;
  readers: number;
  writers: number;
  tier: number;
}

interface TestPhase {
  phase: string;
  name: string;
  modules: string[];
}

interface CriticalPath {
  id: string;
  name: string;
  nameEn: string;
  steps: { formId: string; action: string }[];
}

interface GlobalViewMeta {
  version: number;
  updated: string;
  source: string;
}

interface GlobalViewData {
  meta: GlobalViewMeta;
  businessProcesses: BusinessProcess[];
  modules: Record<string, ModuleInfo>;
  chains: Chain[];
  dependencyMatrix: DependencyMatrix;
  masterData: MasterDataItem[];
  testOrder: TestPhase[];
  criticalPaths: CriticalPath[];
}

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  done: '#52c41a',
  progress: '#faad14',
  notstarted: '#d9d9d9',
};

const STATUS_LABELS: Record<string, string> = {
  done: '已完成',
  progress: '進行中',
  notstarted: '未開始',
};

const CHAIN_STATUS_COLORS: Record<string, string> = {
  LIVE_VERIFIED: '#52c41a',
  INTEGRATION_VERIFIED: '#1890ff',
  PARTIALLY_CONNECTED: '#faad14',
  CHAIN_BROKEN: '#ff4d4f',
  NOT_STARTED: '#d9d9d9',
};

const CHAIN_STATUS_LABELS: Record<string, string> = {
  LIVE_VERIFIED: '線上驗證',
  INTEGRATION_VERIFIED: '整合驗證',
  PARTIALLY_CONNECTED: '部分連接',
  CHAIN_BROKEN: '鏈路斷裂',
  NOT_STARTED: '未開始',
};

const TIER_COLORS: Record<number, string> = {
  1: '#1890ff',
  2: '#52c41a',
  3: '#d9d9d9',
};

const TIER_LABELS: Record<number, string> = {
  1: 'Tier 1 (核心)',
  2: 'Tier 2 (常用)',
  3: 'Tier 3 (輔助)',
};

const DOMAIN_GROUP_ORDER = [
  'Sales', 'Purchase', 'Inventory', 'Production', 'R&D',
  'Finance', 'QC', 'Service', 'HR', 'Admin', 'System',
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getStatusTag = (status: string) => (
  <Tag color={STATUS_COLORS[status] || '#d9d9d9'} style={{ borderRadius: 4 }}>
    {STATUS_LABELS[status] || status}
  </Tag>
);

const getStepIcon = (type: string) => {
  switch (type) {
    case 'approval': return <CheckCircleOutlined style={{ color: '#1890ff', marginRight: 4 }} />;
    case 'reversal': return <RollbackOutlined style={{ color: '#faad14', marginRight: 4 }} />;
    default: return null;
  }
};

const getCellBg = (value: number, isDiag: boolean): string => {
  if (isDiag) return '#e6f7ff';
  if (value === 0) return '#ffffff';
  if (value <= 2) return '#fffbe6';
  if (value <= 5) return '#fff7e6';
  return '#fff1f0';
};

const getCellColor = (value: number, isDiag: boolean): string => {
  if (isDiag) return '#1890ff';
  if (value === 0) return '#d9d9d9';
  if (value <= 2) return '#d48806';
  if (value <= 5) return '#d4380d';
  return '#cf1322';
};

// ─── Tab 1: Business Process Overview ────────────────────────────────────────

const BusinessProcessOverview: React.FC<{
  data: GlobalViewData;
  onModuleClick: (formId: string) => void;
}> = ({ data, onModuleClick }) => {
  const stats = useMemo(() => {
    const allModules = Object.values(data.modules);
    const total = allModules.length;
    const done = allModules.filter(m => m.status === 'done').length;
    const inProgress = allModules.filter(m => m.status === 'progress').length;
    return { total, done, inProgress, notStarted: total - done - inProgress };
  }, [data.modules]);

  return (
    <div>
      {/* Summary Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic title="全部模組" value={stats.total} prefix={<ClusterOutlined />} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="已完成" value={stats.done} valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />} suffix={<span style={{ fontSize: 14, color: '#999' }}>/ {stats.total}</span>} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="進行中" value={stats.inProgress} valueStyle={{ color: '#faad14' }}
              prefix={<NodeIndexOutlined />} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="完成率" value={stats.total > 0 ? Math.round((stats.done / stats.total) * 100) : 0}
              suffix="%" valueStyle={{ color: '#1890ff' }}
              prefix={<FundProjectionScreenOutlined />} />
          </Card>
        </Col>
      </Row>

      {/* Business Process Swimlanes */}
      {data.businessProcesses.map(proc => (
        <Card key={proc.id} size="small"
          style={{ marginBottom: 16, borderLeft: `4px solid ${proc.color}` }}
          title={
            <Space>
              <span style={{ fontWeight: 600 }}>{proc.name}</span>
              <Text type="secondary" style={{ fontSize: 13 }}>{proc.nameEn}</Text>
            </Space>
          }
          extra={<Text type="secondary" style={{ fontSize: 12 }}>{proc.description}</Text>}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, overflowX: 'auto', padding: '8px 0' }}>
            {proc.steps.map((step, idx) => (
              <React.Fragment key={step.formId}>
                {idx > 0 && (
                  <RightOutlined style={{ color: '#bfbfbf', fontSize: 12, flexShrink: 0 }} />
                )}
                <Tooltip title={`${step.formId} - ${step.name} (${STATUS_LABELS[step.status] || step.status})`}>
                  <div
                    onClick={() => onModuleClick(step.formId)}
                    style={{
                      cursor: 'pointer',
                      padding: '6px 12px',
                      borderRadius: 6,
                      border: step.isHub
                        ? '2px solid #faad14'
                        : `1px solid ${STATUS_COLORS[step.status] || '#d9d9d9'}`,
                      background: step.isHub ? '#fffbe6' : '#fafafa',
                      boxShadow: step.isHub ? '0 0 6px rgba(250,173,20,0.3)' : undefined,
                      whiteSpace: 'nowrap',
                      minWidth: 80,
                      textAlign: 'center' as const,
                      transition: 'box-shadow 0.2s',
                    }}
                  >
                    <div style={{ fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {getStepIcon(step.type)}
                      <span style={{ fontWeight: 500 }}>{step.name}</span>
                    </div>
                    <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>{step.formId}</div>
                    <div style={{ marginTop: 4 }}>
                      <Badge color={STATUS_COLORS[step.status] || '#d9d9d9'} text={
                        <span style={{ fontSize: 10, color: '#666' }}>{STATUS_LABELS[step.status] || step.status}</span>
                      } />
                    </div>
                  </div>
                </Tooltip>
              </React.Fragment>
            ))}
          </div>
        </Card>
      ))}

      <Divider orientation="left">Master Data Foundation</Divider>

      {/* Master Data Tags */}
      <Card size="small" style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 8 }}>
          {[1, 2, 3].map(tier => (
            <Tag key={tier} color={TIER_COLORS[tier]} style={{ marginBottom: 4 }}>
              {TIER_LABELS[tier]}
            </Tag>
          ))}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {data.masterData.map(md => (
            <Tooltip key={md.table} title={`${md.table} | Readers: ${md.readers} | Writers: ${md.writers}${md.formId ? ` | Form: ${md.formId}` : ''}`}>
              <Tag
                color={TIER_COLORS[md.tier] || '#d9d9d9'}
                style={{ cursor: 'default', borderRadius: 4, marginBottom: 4 }}
              >
                {md.name} ({md.readers}R)
              </Tag>
            </Tooltip>
          ))}
        </div>
      </Card>

      {/* Critical Integration Paths */}
      {data.criticalPaths.length > 0 && (
        <>
          <Divider orientation="left">Critical Integration Paths</Divider>
          <Collapse
            items={data.criticalPaths.map(cp => ({
              key: cp.id,
              label: (
                <Space>
                  <LinkOutlined />
                  <span style={{ fontWeight: 500 }}>{cp.name}</span>
                  <Text type="secondary" style={{ fontSize: 12 }}>{cp.nameEn}</Text>
                </Space>
              ),
              children: (
                <Steps
                  size="small"
                  direction="horizontal"
                  items={cp.steps.map(s => ({
                    title: s.formId,
                    description: s.action,
                    status: 'process' as const,
                  }))}
                  style={{ overflowX: 'auto' }}
                />
              ),
            }))}
          />
        </>
      )}
    </div>
  );
};

// ─── Tab 2: Module Explorer ──────────────────────────────────────────────────

const ModuleExplorer: React.FC<{ data: GlobalViewData }> = ({ data }) => {
  const [searchText, setSearchText] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const moduleEntries = useMemo(() =>
    Object.entries(data.modules).map(([id, mod]) => ({ id, ...mod })),
    [data.modules],
  );

  const filteredModules = useMemo(() => {
    if (!searchText.trim()) return moduleEntries;
    const q = searchText.toLowerCase();
    return moduleEntries.filter(m =>
      m.id.toLowerCase().includes(q) || m.name.toLowerCase().includes(q),
    );
  }, [moduleEntries, searchText]);

  const groupedByDomain = useMemo(() => {
    const groups: Record<string, typeof filteredModules> = {};
    filteredModules.forEach(m => {
      const domain = m.domain || 'Other';
      if (!groups[domain]) groups[domain] = [];
      groups[domain].push(m);
    });
    return groups;
  }, [filteredModules]);

  const selectedModule = useMemo(() => {
    if (!selectedId) return null;
    const mod = data.modules[selectedId];
    return mod ? { id: selectedId, ...mod } : null;
  }, [selectedId, data.modules]);

  const stats = useMemo(() => ({
    total: moduleEntries.length,
    done: moduleEntries.filter(m => m.status === 'done').length,
    domains: new Set(moduleEntries.map(m => m.domain)).size,
  }), [moduleEntries]);

  const handleSearch = useCallback((value: string) => {
    setSearchText(value);
    setSelectedId(null);
  }, []);

  const handleModuleClick = useCallback((id: string) => {
    setSelectedId(prev => prev === id ? null : id);
  }, []);

  const getRelationColor = (relation: string): string => {
    if (relation.toLowerCase().includes('master')) return '#1890ff';
    if (relation.toLowerCase().includes('cross')) return '#ff4d4f';
    return '#595959';
  };

  // If a module is selected, show the detail view
  if (selectedModule) {
    return (
      <div>
        <div style={{ marginBottom: 16 }}>
          <Button type="link" onClick={() => setSelectedId(null)} icon={<ArrowLeftOutlined />}>
            返回模組列表
          </Button>
          <Input.Search
            placeholder="搜尋模組 (formId / 中文名稱)..."
            allowClear
            style={{ width: 300, marginLeft: 16 }}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onSearch={handleSearch}
          />
        </div>

        <Row gutter={16}>
          {/* Left: Upstream */}
          <Col xs={24} md={7}>
            <Card size="small" title={<Space><SwapOutlined rotate={180} /><span>Upstream ({selectedModule.upstream.length})</span></Space>}
              style={{ height: '100%' }}>
              {selectedModule.upstream.length === 0 ? <Empty description="No upstream" image={Empty.PRESENTED_IMAGE_SIMPLE} /> :
                selectedModule.upstream.map((u, i) => (
                  <Card key={i} size="small" style={{ marginBottom: 8, cursor: 'pointer', borderLeft: `3px solid ${getRelationColor(u.relation)}` }}
                    onClick={() => data.modules[u.formId] && handleModuleClick(u.formId)}>
                    <div style={{ fontWeight: 500 }}>{u.name}</div>
                    <div style={{ fontSize: 11, color: '#999' }}>{u.formId}</div>
                    <div style={{ marginTop: 4 }}>
                      {u.tables.map(t => <Tag key={t} style={{ fontSize: 10, marginBottom: 2 }}>{t}</Tag>)}
                    </div>
                    <Tag color={getRelationColor(u.relation)} style={{ fontSize: 10, marginTop: 4 }}>{u.relation}</Tag>
                  </Card>
                ))
              }
            </Card>
          </Col>

          {/* Center: Module Detail */}
          <Col xs={24} md={10}>
            <Card size="small" title={
              <Space>
                <span style={{ fontWeight: 600, fontSize: 16 }}>{selectedModule.name}</span>
                {getStatusTag(selectedModule.status)}
              </Space>
            }>
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="Form ID">{selectedModule.id}</Descriptions.Item>
                <Descriptions.Item label="Domain">{selectedModule.domain}</Descriptions.Item>
                <Descriptions.Item label="Placement">{selectedModule.placement}</Descriptions.Item>
                <Descriptions.Item label="Chains">
                  {selectedModule.chains.length > 0
                    ? selectedModule.chains.map(c => <Tag key={c} color="blue" style={{ marginBottom: 2 }}>{c}</Tag>)
                    : <Text type="secondary">-</Text>}
                </Descriptions.Item>
                <Descriptions.Item label="Write Tables">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {selectedModule.writeTables.length > 0
                      ? selectedModule.writeTables.map(t => <Tag key={t} color="red" style={{ fontSize: 10 }}>{t}</Tag>)
                      : <Text type="secondary">-</Text>}
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Read Tables">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {selectedModule.readTables.length > 0
                      ? selectedModule.readTables.map(t => <Tag key={t} color="green" style={{ fontSize: 10 }}>{t}</Tag>)
                      : <Text type="secondary">-</Text>}
                  </div>
                </Descriptions.Item>
              </Descriptions>

              {/* Approval Chain */}
              {selectedModule.approvalChain && selectedModule.approvalChain.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <Divider orientation="left" style={{ fontSize: 13 }}>Approval Chain</Divider>
                  <Steps
                    size="small"
                    direction="vertical"
                    current={-1}
                    items={selectedModule.approvalChain.map(ac => ({
                      title: <span>{ac.formId} - {ac.action}</span>,
                      description: <Text type="secondary" style={{ fontSize: 11 }}>{ac.statusField} = {ac.value}</Text>,
                    }))}
                  />
                </div>
              )}
            </Card>
          </Col>

          {/* Right: Downstream */}
          <Col xs={24} md={7}>
            <Card size="small" title={<Space><SwapOutlined /><span>Downstream ({selectedModule.downstream.length})</span></Space>}
              style={{ height: '100%' }}>
              {selectedModule.downstream.length === 0 ? <Empty description="No downstream" image={Empty.PRESENTED_IMAGE_SIMPLE} /> :
                selectedModule.downstream.map((d, i) => (
                  <Card key={i} size="small" style={{ marginBottom: 8, cursor: 'pointer', borderLeft: `3px solid ${getRelationColor(d.relation)}` }}
                    onClick={() => data.modules[d.formId] && handleModuleClick(d.formId)}>
                    <div style={{ fontWeight: 500 }}>{d.name}</div>
                    <div style={{ fontSize: 11, color: '#999' }}>{d.formId}</div>
                    <div style={{ marginTop: 4 }}>
                      {d.tables.map(t => <Tag key={t} style={{ fontSize: 10, marginBottom: 2 }}>{t}</Tag>)}
                    </div>
                    <Tag color={getRelationColor(d.relation)} style={{ fontSize: 10, marginTop: 4 }}>{d.relation}</Tag>
                  </Card>
                ))
              }
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  // Default: module grid view grouped by domain
  return (
    <div>
      {/* Summary + Search */}
      <Row gutter={16} style={{ marginBottom: 16 }} align="middle">
        <Col flex="auto">
          <Space size="large">
            <Statistic title="模組總數" value={stats.total} />
            <Statistic title="已完成" value={stats.done} valueStyle={{ color: '#52c41a' }} />
            <Statistic title="域" value={stats.domains} />
          </Space>
        </Col>
        <Col>
          <Input.Search
            placeholder="搜尋模組 (formId / 中文名稱)..."
            allowClear
            style={{ width: 320 }}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onSearch={handleSearch}
          />
        </Col>
      </Row>

      {Object.keys(groupedByDomain).length === 0 ? (
        <Empty description="No modules found" />
      ) : (
        Object.entries(groupedByDomain)
          .sort(([a], [b]) => {
            const ai = DOMAIN_GROUP_ORDER.indexOf(a);
            const bi = DOMAIN_GROUP_ORDER.indexOf(b);
            return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
          })
          .map(([domain, mods]) => (
            <div key={domain} style={{ marginBottom: 24 }}>
              <Divider orientation="left">
                <Space>
                  <ApartmentOutlined />
                  <span style={{ fontWeight: 600 }}>{domain}</span>
                  <Text type="secondary">({mods.length})</Text>
                </Space>
              </Divider>
              <Row gutter={[12, 12]}>
                {mods.map(m => (
                  <Col key={m.id} xs={12} sm={8} md={6} lg={4}>
                    <Card
                      size="small"
                      hoverable
                      onClick={() => handleModuleClick(m.id)}
                      style={{
                        borderLeft: `3px solid ${STATUS_COLORS[m.status] || '#d9d9d9'}`,
                        height: '100%',
                      }}
                    >
                      <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 4 }}>{m.name}</div>
                      <div style={{ fontSize: 11, color: '#999' }}>{m.id}</div>
                      <div style={{ marginTop: 6 }}>
                        <Badge color={STATUS_COLORS[m.status] || '#d9d9d9'} text={
                          <span style={{ fontSize: 10, color: '#666' }}>{STATUS_LABELS[m.status] || m.status}</span>
                        } />
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          ))
      )}
    </div>
  );
};

// ─── Tab 3: Dependency Matrix ────────────────────────────────────────────────

const DependencyMatrixView: React.FC<{ data: GlobalViewData }> = ({ data }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerEdges, setDrawerEdges] = useState<DependencyEdge[]>([]);
  const [drawerTitle, setDrawerTitle] = useState('');

  const { domains, domainLabels, matrix, edges } = data.dependencyMatrix;

  const totals = useMemo(() => {
    const outgoing = domains.map((_, ri) =>
      matrix[ri]?.reduce((s, v) => s + v, 0) || 0,
    );
    const incoming = domains.map((_, ci) =>
      matrix.reduce((s, row) => s + (row[ci] || 0), 0),
    );
    return { outgoing, incoming };
  }, [domains, matrix]);

  const totalEdges = useMemo(() => edges.length, [edges]);

  const handleCellClick = useCallback((rowIdx: number, colIdx: number) => {
    const fromDomain = domains[rowIdx];
    const toDomain = domains[colIdx];
    const cellEdges = edges.filter(e => e.fromDomain === fromDomain && e.toDomain === toDomain);
    if (cellEdges.length === 0) return;
    setDrawerTitle(`${domainLabels[fromDomain] || fromDomain} -> ${domainLabels[toDomain] || toDomain}`);
    setDrawerEdges(cellEdges);
    setDrawerOpen(true);
  }, [domains, edges, domainLabels]);

  const columns = useMemo(() => {
    const cols: any[] = [
      {
        title: '',
        dataIndex: 'label',
        key: 'label',
        fixed: 'left' as const,
        width: 100,
        render: (text: string) => <Text strong style={{ fontSize: 12 }}>{text}</Text>,
      },
      ...domains.map((d, ci) => ({
        title: <Tooltip title={d}><span style={{ fontSize: 11 }}>{domainLabels[d] || d}</span></Tooltip>,
        dataIndex: d,
        key: d,
        width: 80,
        align: 'center' as const,
        onCell: (_: any, ri: number | undefined) => {
          const rowIdx = ri ?? 0;
          const val = matrix[rowIdx]?.[ci] || 0;
          const isDiag = rowIdx === ci;
          return {
            style: {
              background: getCellBg(val, isDiag),
              cursor: val > 0 ? 'pointer' : 'default',
              fontWeight: val > 0 ? 600 : 400,
              color: getCellColor(val, isDiag),
              textAlign: 'center' as const,
              fontSize: 13,
            },
            onClick: () => val > 0 && handleCellClick(rowIdx, ci),
          };
        },
      })),
      {
        title: <Text type="secondary" style={{ fontSize: 11 }}>Out</Text>,
        dataIndex: 'outTotal',
        key: 'outTotal',
        width: 60,
        align: 'center' as const,
        render: (v: number) => <Text strong style={{ fontSize: 12, color: '#1890ff' }}>{v}</Text>,
      },
    ];
    return cols;
  }, [domains, domainLabels, matrix, handleCellClick]);

  const tableData = useMemo(() => {
    return domains.map((d, ri) => {
      const row: Record<string, any> = {
        key: d,
        label: domainLabels[d] || d,
        outTotal: totals.outgoing[ri],
      };
      domains.forEach((td, ci) => {
        row[td] = matrix[ri]?.[ci] || 0;
      });
      return row;
    });
  }, [domains, domainLabels, matrix, totals]);

  // Add a totals row
  const dataWithTotals = useMemo(() => {
    const totalRow: Record<string, any> = { key: '__totals__', label: 'In Total', outTotal: '' };
    domains.forEach((d, ci) => {
      totalRow[d] = totals.incoming[ci];
    });
    return [...tableData, totalRow];
  }, [tableData, domains, totals]);

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card size="small">
            <Statistic title="Domain 數量" value={domains.length} prefix={<ApartmentOutlined />} />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic title="依賴邊數" value={totalEdges} prefix={<SwapOutlined />} />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic title="跨域依賴" value={edges.filter(e => e.fromDomain !== e.toDomain).length}
              prefix={<LinkOutlined />} valueStyle={{ color: '#ff4d4f' }} />
          </Card>
        </Col>
      </Row>

      {/* Legend */}
      <div style={{ marginBottom: 12 }}>
        <Space size="middle">
          <Text type="secondary" style={{ fontSize: 12 }}>Legend:</Text>
          {[
            { bg: '#ffffff', border: '#f0f0f0', label: '0' },
            { bg: '#fffbe6', border: '#ffe58f', label: '1-2' },
            { bg: '#fff7e6', border: '#ffd591', label: '3-5' },
            { bg: '#fff1f0', border: '#ffa39e', label: '6+' },
            { bg: '#e6f7ff', border: '#91d5ff', label: 'Same domain' },
          ].map((item, i) => (
            <Space key={i} size={4}>
              <div style={{ width: 16, height: 16, background: item.bg, border: `1px solid ${item.border}`, borderRadius: 2 }} />
              <Text style={{ fontSize: 11 }}>{item.label}</Text>
            </Space>
          ))}
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={dataWithTotals}
        pagination={false}
        bordered
        size="small"
        scroll={{ x: 'max-content' }}
        rowClassName={(record) => record.key === '__totals__' ? 'matrix-totals-row' : ''}
      />

      <Drawer
        title={<Space><InfoCircleOutlined /><span>{drawerTitle}</span></Space>}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={520}
      >
        {drawerEdges.length === 0 ? <Empty /> : (
          <div>
            <Text type="secondary" style={{ marginBottom: 12, display: 'block' }}>
              {drawerEdges.length} dependency edge(s)
            </Text>
            {drawerEdges.map((edge, i) => (
              <Card key={i} size="small" style={{ marginBottom: 12, borderLeft: '3px solid #1890ff' }}>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="From">
                    <Tag>{edge.from}</Tag>
                    <Text type="secondary" style={{ fontSize: 11 }}>({edge.fromDomain})</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="To">
                    <Tag>{edge.to}</Tag>
                    <Text type="secondary" style={{ fontSize: 11 }}>({edge.toDomain})</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Tables">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {edge.tables.map(t => <Tag key={t} color="blue" style={{ fontSize: 10 }}>{t}</Tag>)}
                    </div>
                  </Descriptions.Item>
                  <Descriptions.Item label="Meaning">
                    {edge.meaning}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            ))}
          </div>
        )}
      </Drawer>
    </div>
  );
};

// ─── Tab 4: Chain Progress ───────────────────────────────────────────────────

const ChainProgress: React.FC<{ data: GlobalViewData }> = ({ data }) => {
  const groupedChains = useMemo(() => {
    const groups: Record<string, Chain[]> = {};
    data.chains.forEach(c => {
      const domain = c.domain || 'Other';
      if (!groups[domain]) groups[domain] = [];
      groups[domain].push(c);
    });
    return groups;
  }, [data.chains]);

  const stats = useMemo(() => {
    const total = data.chains.length;
    const live = data.chains.filter(c => c.status === 'LIVE_VERIFIED').length;
    const integrated = data.chains.filter(c => c.status === 'INTEGRATION_VERIFIED').length;
    const totalModules = data.chains.reduce((s, c) => s + c.moduleCount, 0);
    const totalDone = data.chains.reduce((s, c) => s + c.doneCount, 0);
    return { total, live, integrated, totalModules, totalDone };
  }, [data.chains]);

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={5}>
          <Card size="small">
            <Statistic title="鏈路總數" value={stats.total} prefix={<LinkOutlined />} />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small">
            <Statistic title="線上驗證" value={stats.live} valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />} />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small">
            <Statistic title="整合驗證" value={stats.integrated} valueStyle={{ color: '#1890ff' }}
              prefix={<NodeIndexOutlined />} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title="鏈路模組" value={stats.totalModules} prefix={<ClusterOutlined />} />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small">
            <Statistic title="鏈路完成率" suffix="%"
              value={stats.totalModules > 0 ? Math.round((stats.totalDone / stats.totalModules) * 100) : 0}
              valueStyle={{ color: '#1890ff' }}
              prefix={<FundProjectionScreenOutlined />} />
          </Card>
        </Col>
      </Row>

      {Object.entries(groupedChains)
        .sort(([a], [b]) => {
          const ai = DOMAIN_GROUP_ORDER.indexOf(a);
          const bi = DOMAIN_GROUP_ORDER.indexOf(b);
          return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
        })
        .map(([domain, chains]) => (
          <Card key={domain} size="small" title={
            <Space><DatabaseOutlined /><span style={{ fontWeight: 600 }}>{domain}</span>
              <Text type="secondary">({chains.length} chains)</Text></Space>
          } style={{ marginBottom: 16 }}>
            <Collapse
              items={chains.map(chain => {
                const pct = chain.moduleCount > 0 ? Math.round((chain.doneCount / chain.moduleCount) * 100) : 0;
                return {
                  key: chain.id,
                  label: (
                    <Row align="middle" gutter={12} style={{ width: '100%' }}>
                      <Col flex="200px">
                        <Text strong style={{ fontSize: 13 }}>{chain.name}</Text>
                      </Col>
                      <Col flex="100px">
                        <Tag color={CHAIN_STATUS_COLORS[chain.status] || '#d9d9d9'} style={{ borderRadius: 4 }}>
                          {CHAIN_STATUS_LABELS[chain.status] || chain.status}
                        </Tag>
                      </Col>
                      <Col flex="auto">
                        <Progress
                          percent={pct}
                          size="small"
                          strokeColor={CHAIN_STATUS_COLORS[chain.status] || '#d9d9d9'}
                          format={() => `${chain.doneCount}/${chain.moduleCount}`}
                        />
                      </Col>
                    </Row>
                  ),
                  children: (
                    <div>
                      <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>{chain.description}</Text>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {chain.modules.map(modId => {
                          const mod = data.modules[modId];
                          const color = mod ? STATUS_COLORS[mod.status] : '#d9d9d9';
                          const name = mod?.name || modId;
                          return (
                            <Tooltip key={modId} title={`${modId} - ${name} (${mod ? STATUS_LABELS[mod.status] : 'unknown'})`}>
                              <Tag color={color} style={{ borderRadius: 4 }}>
                                {name}
                              </Tag>
                            </Tooltip>
                          );
                        })}
                      </div>
                    </div>
                  ),
                };
              })}
            />
          </Card>
        ))}
    </div>
  );
};

// ─── Main Page Component ─────────────────────────────────────────────────────

const GlobalViewPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<GlobalViewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('process');

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch('/erp-global-view.json')
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        return res.json();
      })
      .then((json: GlobalViewData) => {
        setData(json);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load global view data:', err);
        setError(err.message || 'Failed to load data');
        setLoading(false);
        message.error('Failed to load ERP global view data');
      });
  }, []);

  const handleModuleClickFromProcess = useCallback((formId: string) => {
    setActiveTab('explorer');
    // Small delay to allow tab switch, then trigger search
    setTimeout(() => {
      // The Module Explorer will pick up the selected module via its own state
      // For cross-tab navigation, we encode the formId in the tab key
    }, 0);
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', height: '60vh', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" tip="Loading ERP global view data..." />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Empty
          description={
            <span>
              <Text type="danger">{error || 'No data available'}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Ensure <code>/erp-global-view.json</code> is available in the public folder.
              </Text>
            </span>
          }
        />
        <Button type="primary" onClick={() => navigate('/erp')} style={{ marginTop: 16 }}>
          返回 ERP
        </Button>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px 24px', background: '#fff', minHeight: '100%' }}>
      {/* Page Header */}
      <div style={{ marginBottom: 20 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/erp')}
          style={{ padding: 0, marginBottom: 8 }}
        >
          返回 ERP
        </Button>
        <Title level={3} style={{ marginBottom: 0 }}>
          <TableOutlined style={{ marginRight: 8 }} />
          ERP 全局視角
        </Title>
        <Text type="secondary">
          模組依賴關係 &middot; 商業流程 &middot; 開發進度
        </Text>
        {data.meta && (
          <Text type="secondary" style={{ display: 'block', fontSize: 11, marginTop: 4 }}>
            v{data.meta.version} &middot; Updated: {data.meta.updated} &middot; Source: {data.meta.source}
          </Text>
        )}
      </div>

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        type="card"
        items={[
          {
            key: 'process',
            label: (
              <Space>
                <NodeIndexOutlined />
                <span>商業流程總覽</span>
              </Space>
            ),
            children: <BusinessProcessOverview data={data} onModuleClick={handleModuleClickFromProcess} />,
          },
          {
            key: 'explorer',
            label: (
              <Space>
                <SearchOutlined />
                <span>模組探索器</span>
              </Space>
            ),
            children: <ModuleExplorer data={data} />,
          },
          {
            key: 'matrix',
            label: (
              <Space>
                <TableOutlined />
                <span>依賴矩陣</span>
              </Space>
            ),
            children: <DependencyMatrixView data={data} />,
          },
          {
            key: 'chains',
            label: (
              <Space>
                <LinkOutlined />
                <span>鏈路進度</span>
              </Space>
            ),
            children: <ChainProgress data={data} />,
          },
        ]}
      />

      {/* Inline styles for the totals row */}
      <style>{`
        .matrix-totals-row td {
          background: #fafafa !important;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
};

export default GlobalViewPage;
